import { useMemo } from 'react'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  TrendingUp,
  AlertCircle,
  Users,
  FileText,
  Sparkles,
  ChevronRight,
  BarChart3,
  CheckSquare,
  Lightbulb,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import type { Interview, PatternCluster, FacetType } from '@/types/interviewer'
import { FACET_TYPE_CONFIG } from '@/types/interviewer'
import { clusterFacets, getAggregatedStats } from '@/lib/interviewer/clustering'
import { DecisionTimeline } from './DecisionTimeline'
import { StakeholderMap } from './StakeholderMap'

interface InsightsPanelProps {
  interviews: Interview[]
  onSelectCluster?: (cluster: PatternCluster) => void
  onSelectInterview?: (interview: Interview) => void
}

// Helper to get facet color
function getFacetColor(type: string): string {
  return FACET_TYPE_CONFIG[type as FacetType]?.color || '#6c757d'
}

export function InsightsPanel({
  interviews,
  onSelectCluster,
  onSelectInterview,
}: InsightsPanelProps) {
  const stats = useMemo(() => getAggregatedStats(interviews), [interviews])
  const clusters = useMemo(() => clusterFacets(interviews), [interviews])

  // Calculate decision-specific stats
  const decisionStats = useMemo(() => {
    const decisionInterviews = interviews.filter(
      i => i.mode === 'decisions' && i.status === 'analyzed'
    )

    const rationaleScores = decisionInterviews
      .map(i => i.scores.rationaleQuality)
      .filter((s): s is number => s !== undefined)

    const contextScores = decisionInterviews
      .map(i => i.scores.contextPreservation)
      .filter((s): s is number => s !== undefined)

    const totalDecisions = decisionInterviews.reduce((count, i) =>
      count + i.facets.filter(f => f.type === 'decision' && !f.dismissed).length
    , 0)

    const oneWayDoors = decisionInterviews.reduce((count, i) =>
      count + i.facets.filter(f => f.type === 'decision' && f.reversibility === 'one-way-door' && !f.dismissed).length
    , 0)

    return {
      count: decisionInterviews.length,
      totalDecisions,
      oneWayDoors,
      avgRationale: rationaleScores.length > 0
        ? rationaleScores.reduce((a, b) => a + b, 0) / rationaleScores.length
        : 0,
      avgContext: contextScores.length > 0
        ? contextScores.reduce((a, b) => a + b, 0) / contextScores.length
        : 0,
    }
  }, [interviews])

  const emergingPatterns = clusters.filter(c => c.emergingPattern)
  const topClusters = clusters.slice(0, 5)

  // Check if we're primarily showing decisions
  const hasDecisions = decisionStats.count > 0
  const isDecisionFocused = interviews.every(i => i.mode === 'decisions')

  if (stats.totalInterviews === 0) {
    return (
      <Card className="p-6 text-center">
        <Sparkles className="w-10 h-10 text-text-muted mx-auto mb-3" />
        <h3 className="font-medium mb-1">No insights yet</h3>
        <p className="text-sm text-text-muted">
          Analyze some interviews to see patterns emerge
        </p>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      {/* Stats Overview - Adaptive based on content */}
      <div className="grid grid-cols-4 gap-3">
        <StatCard
          icon={FileText}
          label="Interviews"
          value={stats.totalInterviews}
          color="var(--color-solution)"
        />
        <StatCard
          icon={BarChart3}
          label="Facets"
          value={stats.totalFacets}
          color="var(--color-business)"
        />
        {isDecisionFocused || !stats.avgMomTestScore ? (
          <>
            <StatCard
              icon={Lightbulb}
              label="Avg Rationale"
              value={decisionStats.avgRationale.toFixed(1)}
              color={decisionStats.avgRationale >= 3.5 ? 'var(--color-business)' : 'var(--color-warning)'}
            />
            <StatCard
              icon={CheckSquare}
              label="Decisions"
              value={decisionStats.totalDecisions}
              color="var(--color-solution)"
            />
          </>
        ) : (
          <>
            <StatCard
              icon={AlertCircle}
              label="Avg Mom Test"
              value={stats.avgMomTestScore.toFixed(1)}
              color={stats.avgMomTestScore >= 3.5 ? 'var(--color-business)' : 'var(--color-warning)'}
            />
            <StatCard
              icon={TrendingUp}
              label="Avg Evidence"
              value={stats.avgEvidenceStrength.toFixed(1)}
              color={stats.avgEvidenceStrength >= 3.5 ? 'var(--color-business)' : 'var(--color-warning)'}
            />
          </>
        )}
      </div>

      {/* Decision Timeline (when decisions exist) */}
      {hasDecisions && (
        <DecisionTimeline
          interviews={interviews}
          onSelectInterview={onSelectInterview}
        />
      )}

      {/* Stakeholder Map (when decisions exist) */}
      {hasDecisions && (
        <StakeholderMap
          interviews={interviews}
          onSelectInterview={onSelectInterview}
        />
      )}

      {/* Facet Distribution */}
      <Card className="p-4">
        <h3 className="text-sm font-medium mb-3">Facet Distribution</h3>
        <div className="flex flex-wrap gap-2">
          {Object.entries(stats.facetsByType)
            .sort(([, a], [, b]) => b - a)
            .map(([type, count]) => (
              <Badge
                key={type}
                variant="outline"
                className="text-xs"
                style={{
                  borderColor: getFacetColor(type),
                  color: getFacetColor(type),
                }}
              >
                {type.replace(/_/g, ' ')}: {count}
              </Badge>
            ))}
        </div>
      </Card>

      {/* Emerging Patterns */}
      {emergingPatterns.length > 0 && (
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-solution" />
            <h3 className="text-sm font-medium">Emerging Patterns</h3>
            <Badge variant="secondary" className="text-xs">
              {emergingPatterns.length} found
            </Badge>
          </div>
          <div className="space-y-2">
            {emergingPatterns.slice(0, 3).map((cluster) => (
              <PatternCard
                key={cluster.id}
                cluster={cluster}
                onClick={() => onSelectCluster?.(cluster)}
              />
            ))}
          </div>
        </Card>
      )}

      {/* Top Patterns */}
      {topClusters.length > 0 && (
        <Card className="p-4">
          <h3 className="text-sm font-medium mb-3">Top Patterns by Frequency</h3>
          <div className="space-y-2">
            {topClusters.map((cluster) => (
              <PatternCard
                key={cluster.id}
                cluster={cluster}
                onClick={() => onSelectCluster?.(cluster)}
              />
            ))}
          </div>
        </Card>
      )}

      {/* Top Problems */}
      {stats.topProblems.length > 0 && (
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle className="w-4 h-4 text-problem" />
            <h3 className="text-sm font-medium">Top Validated Problems</h3>
          </div>
          <div className="space-y-2">
            {stats.topProblems.map((facet, idx) => (
              <div
                key={facet.id}
                className="flex items-start gap-2 p-2 rounded-lg bg-warm-50"
              >
                <span className="text-xs text-text-muted font-medium w-5">
                  #{idx + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate">{facet.content}</p>
                  <div className="flex items-center gap-2 mt-1">
                    {facet.evidenceStrength && (
                      <Badge
                        variant="secondary"
                        className={cn(
                          'text-xs',
                          facet.evidenceStrength >= 4 && 'bg-business/10 text-business'
                        )}
                      >
                        Evidence: {facet.evidenceStrength}/5
                      </Badge>
                    )}
                    {facet.severity && (
                      <Badge variant="secondary" className="text-xs capitalize">
                        {facet.severity}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  )
}

function StatCard({
  icon: Icon,
  label,
  value,
  color,
}: {
  icon: typeof FileText
  label: string
  value: string | number
  color: string
}) {
  return (
    <Card className="p-3">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4" style={{ color }} />
        <span className="text-xs text-text-muted">{label}</span>
      </div>
      <p className="text-xl font-semibold" style={{ color }}>
        {value}
      </p>
    </Card>
  )
}

function PatternCard({
  cluster,
  onClick,
}: {
  cluster: PatternCluster
  onClick?: () => void
}) {
  return (
    <button
      onClick={onClick}
      className="w-full text-left p-3 rounded-lg bg-warm-50 hover:bg-warm-100 transition-colors"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-medium truncate">{cluster.name}</span>
            {cluster.emergingPattern && (
              <Sparkles className="w-3 h-3 text-solution flex-shrink-0" />
            )}
          </div>
          <p className="text-xs text-text-muted line-clamp-2">
            {cluster.description}
          </p>
          <div className="flex items-center gap-3 mt-2 text-xs text-text-muted">
            <span className="flex items-center gap-1">
              <Users className="w-3 h-3" />
              {cluster.interviewIds.length} interviews
            </span>
            <span>{cluster.mentionCount} mentions</span>
            {cluster.suggestedEntityType && (
              <Badge variant="outline" className="text-xs">
                → {cluster.suggestedEntityType}
              </Badge>
            )}
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-text-muted flex-shrink-0" />
      </div>
    </button>
  )
}
